/**
 * Hook for checking Chrome 142+ Local Network Access permission before
 * connecting to QZ Tray via loopback WebSocket.
 *
 * Permission states:
 *   'prompt'      – browser will ask; show the request modal first
 *   'denied'      – user blocked it; show the browser-settings instructions modal
 *   'granted'     – already allowed; proceed normally
 *   'unsupported' – browser does not expose this permission; proceed normally
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useState, useRef, useCallback, useEffect } from 'react';
// @ts-ignore
import qz from 'qz-tray';
export var checkLocalNetworkPermission = function () { return __awaiter(void 0, void 0, Promise, function () {
    var result, _a;
    var _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                _c.trys.push([0, 2, , 3]);
                if (typeof navigator === 'undefined' || !((_b = navigator.permissions) === null || _b === void 0 ? void 0 : _b.query))
                    return [2 /*return*/, 'unsupported'];
                return [4 /*yield*/, navigator.permissions.query({
                        name: 'local-network-access'
                    })];
            case 1:
                result = _c.sent();
                return [2 /*return*/, result.state];
            case 2:
                _a = _c.sent();
                return [2 /*return*/, 'unsupported'];
            case 3: return [2 /*return*/];
        }
    });
}); };
/** Attempts to request the local-network-access permission via the Permissions API.
 *  Supported in Firefox only — Chrome does not implement permissions.request(),
 *  so this always returns 'unsupported' in Chrome. The fallback is a direct QZ
 *  WebSocket connect attempt, which is what actually triggers Chrome's native dialog. */
var requestLocalNetworkPermission = function () { return __awaiter(void 0, void 0, Promise, function () {
    var result, _a;
    var _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                _c.trys.push([0, 2, , 3]);
                if (typeof navigator === 'undefined' || !((_b = navigator.permissions) === null || _b === void 0 ? void 0 : _b.request))
                    return [2 /*return*/, 'unsupported'];
                return [4 /*yield*/, navigator.permissions.request({
                        name: 'local-network-access'
                    })];
            case 1:
                result = _c.sent();
                return [2 /*return*/, result.state];
            case 2:
                _a = _c.sent();
                return [2 /*return*/, 'unsupported'];
            case 3: return [2 /*return*/];
        }
    });
}); };
/**
 * @param onRequestPermission
 *   Should attempt a QZ Tray connection (which triggers Chrome's native dialog).
 *   Return true if the connection succeeded (permission granted), false otherwise.
 */
export var useLocalNetworkPermission = function (onRequestPermission) {
    var _a = useState(false), modalVisible = _a[0], setModalVisible = _a[1];
    var _b = useState(null), modalStatus = _b[0], setModalStatus = _b[1];
    // Ref so callbacks can read current status without stale closures
    var modalStatusRef = useRef(null);
    var updateModalStatus = useCallback(function (s) {
        modalStatusRef.current = s;
        setModalStatus(s);
    }, []);
    // Always call the latest callback without it being a dependency
    var requestPermissionRef = useRef(onRequestPermission);
    useEffect(function () {
        requestPermissionRef.current = onRequestPermission;
    });
    var pendingActionRef = useRef(null);
    var guardQzAction = useCallback(function (action) { return __awaiter(void 0, void 0, void 0, function () {
        var status;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, checkLocalNetworkPermission()
                    // Already granted — proceed immediately
                ];
                case 1:
                    status = _b.sent();
                    if (!(status === 'granted')) return [3 /*break*/, 3];
                    return [4 /*yield*/, action()];
                case 2:
                    _b.sent();
                    return [2 /*return*/];
                case 3:
                    if (!(status === 'unsupported' && ((_a = qz === null || qz === void 0 ? void 0 : qz.websocket) === null || _a === void 0 ? void 0 : _a.isActive()))) return [3 /*break*/, 5];
                    return [4 /*yield*/, action()];
                case 4:
                    _b.sent();
                    return [2 /*return*/];
                case 5:
                    // Already denied — show help modal with browser settings links
                    if (status === 'denied') {
                        pendingActionRef.current = action;
                        updateModalStatus('denied');
                        setModalVisible(true);
                        return [2 /*return*/];
                    }
                    // 'prompt' or 'unsupported' (QZ not connected): show our modal first so the
                    // user understands what is about to happen before Chrome's dialog appears.
                    pendingActionRef.current = action;
                    updateModalStatus('prompt');
                    setModalVisible(true);
                    return [2 /*return*/];
            }
        });
    }); }, [updateModalStatus]);
    var handleRequestPermission = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var currentStatus, nativeResult, action, granted_1, action, newStatus, granted, action;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    currentStatus = modalStatusRef.current;
                    if (!(currentStatus === 'prompt')) return [3 /*break*/, 12];
                    return [4 /*yield*/, requestLocalNetworkPermission()];
                case 1:
                    nativeResult = _a.sent();
                    if (!(nativeResult === 'granted')) return [3 /*break*/, 5];
                    setModalVisible(false);
                    updateModalStatus(null);
                    return [4 /*yield*/, requestPermissionRef.current()];
                case 2:
                    _a.sent();
                    action = pendingActionRef.current;
                    pendingActionRef.current = null;
                    if (!action) return [3 /*break*/, 4];
                    return [4 /*yield*/, action()];
                case 3:
                    _a.sent();
                    _a.label = 4;
                case 4: return [2 /*return*/];
                case 5:
                    if (nativeResult === 'denied') {
                        // Switch modal to denied state without closing
                        updateModalStatus('denied');
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, requestPermissionRef.current()];
                case 6:
                    granted_1 = _a.sent();
                    if (!granted_1) return [3 /*break*/, 9];
                    setModalVisible(false);
                    updateModalStatus(null);
                    action = pendingActionRef.current;
                    pendingActionRef.current = null;
                    if (!action) return [3 /*break*/, 8];
                    return [4 /*yield*/, action()];
                case 7:
                    _a.sent();
                    _a.label = 8;
                case 8: return [3 /*break*/, 11];
                case 9: return [4 /*yield*/, checkLocalNetworkPermission()];
                case 10:
                    newStatus = _a.sent();
                    if (newStatus === 'denied') {
                        updateModalStatus('denied');
                    }
                    else {
                        setModalVisible(false);
                        pendingActionRef.current = null;
                    }
                    _a.label = 11;
                case 11: return [2 /*return*/];
                case 12: return [4 /*yield*/, requestPermissionRef.current()];
                case 13:
                    granted = _a.sent();
                    setModalVisible(false);
                    updateModalStatus(null);
                    if (!granted) return [3 /*break*/, 16];
                    action = pendingActionRef.current;
                    pendingActionRef.current = null;
                    if (!action) return [3 /*break*/, 15];
                    return [4 /*yield*/, action()];
                case 14:
                    _a.sent();
                    _a.label = 15;
                case 15: return [3 /*break*/, 17];
                case 16:
                    // Clear pending so the next button press shows the modal again
                    pendingActionRef.current = null;
                    _a.label = 17;
                case 17: return [2 /*return*/];
            }
        });
    }); }, [updateModalStatus]);
    var onCloseModal = useCallback(function () {
        setModalVisible(false);
        pendingActionRef.current = null;
    }, []);
    return {
        guardQzAction: guardQzAction,
        modalVisible: modalVisible,
        modalStatus: modalStatus,
        onRequestPermission: handleRequestPermission,
        onCloseModal: onCloseModal
    };
};
